
import { throwError as observableThrowError, Observable, from, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, tap, timeout } from 'rxjs/operators';
import { Login } from '../models/login';
import { ScreenAuth } from '../models/screen-auth';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // tslint:disable-next-line:ban-types
  config = environment;
  redirectUrl: string;
  userdata: any = {};
  showLoader = false;
  languageId = 1;
  helpData: any;
  vendorId: number;
  statusId: number;
  BioFacialRecUrl="";
  siteId: 0;
  billingRuleId: number;
  professionalId: number;
  confirmdelete = 'detele';
  employerInstutionId: number;
  employerInstutionAddId: number;
  researchMappingId: number;
  empInsProf: string;
  packClientId: number;
  packPrice: number;
  packTatDays: number;
  packageEffective = false;
  packageId = 0;
  ocrRoutes: { [key: string]: string };//environment.ocrRoutes;
  [key: string]: any;
  addOrEdit: string;
  cancelRuleClientId = 0;
  screenAuth: ScreenAuth;
  changePassword = false;
  manageInvitationObj: any;
  clientId = 0;
  pdfType = environment.pdfType;
  defaultSelectedPackageList = [];
  registerComps: any;
  paymentDetails: { Paymentid: number, ApiKey: string; amount: number; prefill: { name: string; email: string; contact: string; }; };
  candidateRegisterFormValue: any;
  dashboardFlag = false;
  /** sessionStorage key holding the cached route prefixes (survives F5). */
  private static readonly API_ROUTE_CONFIG_KEY = 'api_route_config';
  /** Max wait for GetConfiguration. On timeout we fall back to apiUrl and carry on. */
  private static readonly API_ROUTE_CONFIG_TIMEOUT = 10000;
  /**
   * Controllers that must never be redirected to apiUrl1, whatever the table says.
   * These bootstrap the session: routing Login elsewhere would send the very call that
   * fetches this config to the wrong API, and the answer is cached, so a bad row here
   * could lock users out on every subsequent login.
   */
  private static readonly RESERVED_PREFIXES = ['login/', 'captch/', 'auth/'];
  /**
   * Pilot value that opts every user into a flagged controller, instead of naming
   * individual ids. Compared case-insensitively against the trimmed column value.
   */
  private static readonly PILOT_ALL = 'ALL';
  /** Lower-cased route prefixes routed to apiUrl1, e.g. ['case/', 'screening/']. */
  private upgradedControllers: string[] = [];
  constructor(private http: HttpClient) {
    this.restoreApiRouteConfiguration();
  }
  public static handleError(error: any) {
    return observableThrowError(error || 'Server error');
  }

  getApiUrl(apiMethods: string): string {
    if (apiMethods.startsWith(environment.documentUrl)) {
      return apiMethods;
    }
    else if (apiMethods.startsWith('http')) {
      return apiMethods;
    }
    else if (this.isUpgradedController(apiMethods) && this.getConfig('apiUrl1')) {
      // Controller flagged in VtsBaseURLConfiguration -> upgraded API. The apiUrl1 check
      // is deliberate: if that key is missing from the environment, fall through to
      // apiUrl rather than building 'undefined/Case/...'.
      const baseUrl = this.getConfig('apiUrl1');
      return baseUrl + apiMethods;
    }
    else {
      const baseUrl = this.getConfig('apiUrl');
      return baseUrl + apiMethods;
    }
  }

  /**
   * True when the request path targets a controller whose VtsBaseURLConfiguration row
   * has flag = true. Matching is case-insensitive because existing services spell the
   * same controller both ways (e.g. 'verification/...' and 'Verification/...').
   */
  private isUpgradedController(apiMethods: string): boolean {
    if (!this.upgradedControllers.length) {
      return false;
    }
    const lowerUrl = apiMethods.toLowerCase();
    return this.upgradedControllers.some(prefix => lowerUrl.startsWith(prefix));
  }

  /**
   * Fetches the per-controller base URL flags and caches them. Called once per session,
   * after a successful login and before routing to the dashboard — routing must wait on
   * it, otherwise the dashboard's first requests race it and fall back to apiUrl.
   * Lives on Login so it is never itself a flagged controller; were it flagged, this
   * call would need the config it is fetching.
   */
  loadApiRouteConfiguration(): Observable<any> {
    return this.http.get<any[]>('Login/GetConfiguration').pipe(
      // A hung request would otherwise never emit, leaving the caller waiting forever.
      timeout(AuthService.API_ROUTE_CONFIG_TIMEOUT),
      tap(res => this.setApiRouteConfiguration(res)),
      // Covers every failure: HTTP error, timeout, and anything thrown by the tap above.
      // Always emits, so callers gated on this observable still proceed.
      catchError(err => {
        console.error('GetConfiguration failed; all controllers will use apiUrl.', err);
        this.setApiRouteConfiguration([]);
        return of([]);
      })
    );
  }

  /**
   * Turns the API rows into lower-cased route prefixes, e.g. 'Case' -> 'case/'.
   * A row must clear two gates: flag = true (the controller is upgraded at all) and
   * pilot (this user is one of the ones it is upgraded for).
   * Anything unexpected in the payload yields an empty list, which means "use apiUrl
   * for everything" — i.e. exactly the behaviour before this feature existed.
   */
  private setApiRouteConfiguration(rows: any[]): void {
    let prefixes: string[] = [];
    if (Array.isArray(rows)) {
      prefixes = rows
        .filter(f => f && f.flag === true && f.process)
        .filter(f => this.isPilotUser(f.pilot))
        .map(m => String(m.process).trim().toLowerCase() + '/')
        .filter(p => p.length > 1 && AuthService.RESERVED_PREFIXES.indexOf(p) === -1);
    }
    this.upgradedControllers = prefixes;
    // Storage can throw (quota, private browsing). Losing the cache only costs us the
    // routing after a refresh; it must not take the in-memory config down with it.
    try {
      sessionStorage.setItem(AuthService.API_ROUTE_CONFIG_KEY, JSON.stringify(prefixes));
    } catch (e) {
      console.error('Could not cache the API route configuration.', e);
    }
  }

  /**
   * True when a flagged controller applies to the logged-in user, per its pilot column.
   *
   *   'ALL'                -> every user; the controller is fully rolled out
   *   '1234,3221,2334'     -> only those user ids; everyone else stays on apiUrl
   *   null / '' (unset)    -> treated as ALL, so a row nobody has populated behaves
   *                           exactly as it did before the pilot column existed
   *
   * Ids are compared as numbers, not substrings, so 1234 does not match a row that
   * lists 12345. Unparseable entries simply never match.
   */
  private isPilotUser(pilot: any): boolean {
    const value = (pilot === null || pilot === undefined) ? '' : String(pilot).trim();
    if (!value || value.toUpperCase() === AuthService.PILOT_ALL) {
      return true;
    }
    const userId = this.getLoggedUserId();
    if (!userId) {
      // Restricted row and no identifiable user: deny, so the session falls back to
      // apiUrl. Wrongly denying costs nothing; wrongly allowing sends a user to an API
      // that was never rolled out to them.
      return false;
    }
    return value.split(',').some(entry => Number(entry.trim()) === userId);
  }

  /**
   * The logged-in user id. Reads the in-memory copy first — loadSessionData() runs
   * immediately before the config loads at both login entry points — and falls back to
   * sessionStorage so the pilot check cannot depend on that call ordering. Returns 0
   * when no user can be identified.
   */
  private getLoggedUserId(): number {
    let userId = Number(this.userdata && this.userdata.userId);
    if (!userId) {
      try {
        const cached = sessionStorage.getItem('user_data');
        const stored = cached ? JSON.parse(cached) : null;
        userId = Number(stored && stored.userId);
      } catch (e) {
        userId = 0;
      }
    }
    return userId > 0 ? userId : 0;
  }
   getBiometricURL() {
    this.BioFacialRecUrl = environment.BiometricFacialRecognitionUrl;
    return this.BioFacialRecUrl;
  }
  getOCRRoute(key: string) {
    this.ocrRoutes = environment.ocrRoutes;
   return this.ocrRoutes[key];
  }
  getConfig(key: string): any {
  return (environment as any)[key];
  }
  getpdfType(key: string): string {
    return this.pdfType;
  }
  GetUserTeamDetails(userId: number, deptId: number): Observable<any> {
    const dataUrl = 'User/GetUserTeamDetails?userId=' + userId + '&deptId=' + deptId;
    return this.http.get<any>(dataUrl);
  }
  public login(login: Login) {
    // const url = 'auth/login';
    const url = 'Login/login';
    return this.http.post(url, login).pipe(
      catchError(AuthService.handleError)
    );
  }
  // Pricing
  GetDefaultClientComponentDetails(): Observable<any> {
    const dataUrl = 'Auth/GetDefaultClientComponentDetails';
    return this.http.get<any>(dataUrl);
  }
  GetDefaultClientPackageDetails(): Observable<any> {
    const dataUrl = 'Auth/GetDefaultClientPackageDetails';
    return this.http.get<any>(dataUrl);
  }
  AddPaymentCandidateDetails(paymentCandidateVm: any): Observable<any> {
    const dataUrl = 'auth/AddPaymentCandidateDetails';
    return this.http.post<any>(dataUrl, paymentCandidateVm);
  }
  CheckUserName(userName: string,): Observable<any> {
    const dataUrl = 'auth/CheckUserName?userName=' + userName;
    return this.http.get(dataUrl, {
    responseType: 'text'
  });
  }
  DeletePaymentBeforeCandidateCase(userId: number, remarks: string): Observable<any> {
    const dataUrl = 'auth/DeletePaymentBeforeCandidateCase?userId=' + userId + '&remarks=' + remarks;
    return this.http.get<any>(dataUrl);
  }
  //
  GetUserRolePermission(userId: number, applicationId: number, deptId, subTeamId, teamId): Observable<any> {
    deptId = deptId === null ? 0 : deptId;
    const dataUrl = 'User/GetUserRolePermission?userId=' + userId + '&applicationId=' + applicationId + '&deptId=' + deptId + '&subTeamId=' + subTeamId + '&teamId=' + teamId;
    return this.http.get<any>(dataUrl);
  }
  public addChangePassword(password: any): Observable<any> {
    const dataUrl = 'auth/ChangePassword';
    return this.http.post<any>(dataUrl, password);
  }
  public checkPassword(password: any): Observable<any> {
    const dataUrl = 'auth/CheckPassword';
    return this.http.post<any>(dataUrl, password);
  }
  // tslint:disable-next-line:ban-types
  // load(): Promise<Object> {
  //   const promise = this.http.get('./config.json').toPromise();
  //   promise.then(config => this.config = config);

  //   this.http.get('./config.json').toPromise();
  //   promise.then(res => this.pdfType = res);
  //   promise.then(config => {
  //     //this.config = config;
  //     this.ocrRoutes = config["ocrRoutes"];  
  //     //this.pdfType = config["pdfType"] || {}; 
  //   });
  //   this.loadSessionData();
  //   return promise;
  // }

  loadSessionData(): void {
    if (sessionStorage.getItem('user_data')) {
      this.userdata = JSON.parse(sessionStorage.getItem('user_data'));
    }
    if (sessionStorage.getItem('languageId')) {
      this.languageId = JSON.parse(sessionStorage.getItem('languageId'));
    }
    this.restoreApiRouteConfiguration();
  }

  /**
   * Rehydrates the cached route prefixes. GetConfiguration only runs at login, so
   * without this a page refresh would silently send flagged controllers back to apiUrl.
   */
  private restoreApiRouteConfiguration(): void {
    try {
      const cached = sessionStorage.getItem(AuthService.API_ROUTE_CONFIG_KEY);
      if (!cached) {
        return;
      }
      const parsed = JSON.parse(cached);
      this.upgradedControllers = Array.isArray(parsed)
        ? parsed.filter(p => typeof p === 'string' && p.length > 1
            && AuthService.RESERVED_PREFIXES.indexOf(p) === -1)
        : [];
    } catch (e) {
      // Unreadable or corrupt cache -> apiUrl for everything.
      this.upgradedControllers = [];
    }
  }
  getScreenAuth(routerurl: string): ScreenAuth {
    const unAuthorizatio: ScreenAuth = {
      addFlag: false,
      editFlag: false,
      deleteFlag: false,
      viewFlag: false,
      fullAccessFlag: false,
      approveFlag: false,
      moduleId: 0,
      subModuleId: 0,
      screenId: 0,
      screenName: null,
      moduleName: null,
      subModuleName: null,
    };
    const permissionAvailable = JSON.parse(sessionStorage.getItem('user_roles'));
    if (permissionAvailable) {
      this.screenAuth = permissionAvailable.find(f => f.routingUrl === routerurl);
      if (this.screenAuth) {
        return this.screenAuth;
      } else {
        return unAuthorizatio;
      }
    } else {
      return unAuthorizatio;
    }
  }

  LogOut(userId: number, logId: number): Observable<any> {
    const dataUrl = 'Login/LogOut?userId=' + userId + '&logId=' + logId;
    //const dataUrl = 'Auth/LogOut?userId=' + userId + '&logId=' + this.userdata.logId;
    return this.http.post(dataUrl, userId);
  }
  forgetPassword(data: string, applicationId: number): Observable<any> {
    const dataUrl = 'Auth/ForgetPassword?userNameOrMailId=' + data + '&applicationId=' + applicationId;
    return this.http.post(dataUrl, data);
  }
  resetpassword(userId: number): Observable<any> {
    const dataUrl = 'Auth/ResetPassword?userId=' + userId + '&loggedId=' + this.userdata.userId;
    return this.http.post(dataUrl, userId, this.userdata.userId);
  }
  passwordUnlock(userId: number): Observable<any> {
    const dataUrl = 'User/PasswordUnLock?userId=' + userId + '&loggedId=' + this.userdata.userId;
    return this.http.post(dataUrl, userId, this.userdata.userId);
  }
  public GetNumber(userId): Observable<any> {
    const dataUrl = 'auth/GetCandidateMobileNo?userId=' + userId;
    return this.http.get(dataUrl, userId).pipe(
      catchError(AuthService.handleError)
    );
  }
  public otpSend(MobileNumber): Observable<any> {
    const url = 'auth/SendOtpCandidate?MobileNumber=' + MobileNumber;
    return this.http.get(url).pipe(
      catchError(AuthService.handleError)
    );
  }
  public otpVerification(MobileNumber, OTP): Observable<any> {
    const dataUrl = 'auth/CandiateVerifyOtp?MobileNumber=' + MobileNumber + '&VerificationCode=' + OTP;
    return this.http.get(dataUrl).pipe(
      catchError(AuthService.handleError)
    );
  }

  // public ResendOtp(MobileNumber: Login):Observable<any> {
  //   const url = 'auth/ResendOtp';
  //   return this.http.post(url, MobileNumber).pipe(
  //     catchError(AuthService.handleError)
  //   );
  // }
  public mailVerification(userId, emailId, userName, applicationid): Observable<any> {
    const dataUrl = 'Login/SendMailOtpCandidate?UserId=' + userId + '&MailId=' + emailId + '&UserName=' + userName + '&Applicationid=' + applicationid
    return this.http.post(dataUrl, userId).pipe(
      catchError(AuthService.handleError)
    )
  }
  public verifyMailOtp(userId, Otpid): Observable<any> {
    const dataUrl = 'auth/MailVerifyOtp?UserId=' + userId + '&OtpId=' + Otpid
    return this.http.post(dataUrl, userId).pipe(
      catchError(AuthService.handleError)
    )
  }
  // public UpdateMailOtp(userId,Veriifyid):Observable<any>{
  //   const dataUrl='auth/UpdateMailOtpCandidate?UserId='+userId+'&OtpId='+Veriifyid 
  //   return this.http.post(dataUrl,userId).pipe(
  //     catchError(AuthService.handleError)
  //   )
  // }
  addDigitialAddressVerificaton(addressVerification) {
    const dataUrl = 'auth/DigitalAddressVerification';
    return this.http.post<any>(dataUrl, addressVerification);
  }

  //Ajith:- VTS2-2024-CRT-0166 :- create a provisioning to send insufficiencies request raised at Level 1 and Level 2 for Direct App cases directly
  getDirectAppInsuffDetails(ScreeningComid) {
    const dataUrl = 'auth/getDirectAppInsuffDetails?screeningCompId=' + ScreeningComid;
    return this.http.get<any>(dataUrl);
  }
  getDigitialAddressverification(ScreeningComid) {
    const dataUrl = 'auth/GetDigitalAddress?screeningCompId=' + ScreeningComid;
    return this.http.get<any>(dataUrl);
  }
  getUrlExpiryDate(ScreeningComid) {
    const dataUrl = 'auth/GetUrlExpiryDate?screeningCompId=' + ScreeningComid;
    return this.http.get<any>(dataUrl);
  }

  public getDigitialAddressType(): Observable<any> {
    const dataUrl = 'auth/GetDigitalAddressType';
    return this.http.get(dataUrl);
  }
  public Leaverapi(canidatedata) {
    const dataUrl = 'auth/leaverCanidate';
    return this.http.post(dataUrl, canidatedata);
  }
  public getLeaverCanidateDetails(clinetid): Observable<any> {
    const dataUrl = 'auth/GetLeaverCandidateDetails?clientId=' + clinetid;
    return this.http.get(dataUrl);
  }
  public AddUpdatePayment(paymentVm): Observable<any> {
    const dataUrl = 'auth/AddUpdatePayment';
    return this.http.post(dataUrl, paymentVm);
  }
  public getPaymentkey(): Observable<any> {
    const dataUrl = 'auth/PaymentIdGet';
    return this.http.get(dataUrl);
  }
  CheckMailid(mailid: string,): Observable<any> {
    const dataUrl = 'auth/CheckMailid?mailid=' + mailid;
    return this.http.get<any>(dataUrl);
  }
  public CheckClientBasedUserEmail(mailid, clientId): Observable<any> {
    const dataUrl = 'auth/CheckClientBasedUserEmail?mailid=' + mailid + '&clientId=' + clientId;
    return this.http.get<any>(dataUrl);
  }
  getDigilockerClientId(): Observable<string> {
    const dataUrl = 'auth/GetDigilockerClientId';
    return this.http.get<any>(dataUrl);
  }
  GetSSOUserDetails(applicationId: number, emailId: string): Observable<any> {
    const dataUrl = 'User/CheckValidEmailIdForSSO?applicationId=' + applicationId + '&emailId=' + emailId;
    return this.http.get<any>(dataUrl);
  }
  getUserName(credentialsVm): Observable<any> {
    const dataUrl = 'auth/GetUserName';
    return this.http.post<any>(dataUrl, credentialsVm);
  }
}
